import type { DependencyList } from 'react';

import { useCallback, useEffect, useRef } from 'react';

// noinspection ES6PreferShortImport
import { useCacheContext } from '../context/CacheContext';

type FuncType<T extends unknown[], U> = (...args: T) => U;

export const useCacheCallback = <T extends unknown[], R>(
  callback: FuncType<T, Promise<R>>,
  options: {
    /**
     * time in ms, default to 5 minute
     */
    cacheTime?: number;
    /**
     * must generate a globally unique cache key
     */
    getCacheKey: FuncType<T, string>;
  },
  deps: DependencyList,
) => {
  const { getValue, setValue } = useCacheContext();
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  return useCallback(
    (...args: T): Promise<R> => {
      const { cacheTime = 1000 * 60 * 5, getCacheKey } = optionsRef.current;

      const key = getCacheKey(...args);
      let promise = getValue<Promise<R>>(key);
      if (!promise) {
        promise = callback(...args);
        setValue(key, promise, { ttl: cacheTime });
      }
      return promise;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps,react-hooks/use-memo
    [callback, ...deps],
  );
};
